import { capitalize } from '@/helpers/string-helpers'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useGridTabsContext } from '@/scopes/prices/components/GridTabsContext'
import { PricesUseCasesContext } from '@/scopes/prices/components/PricesUseCasesContext'
import { NavigationByWeeksContext } from '@/scopes/prices/components/PriceTableByWeeks/NavigationByWeeksContext'
import { getPriceRangeSelector } from '@/scopes/prices/components/selectors'
import { createPricesFilterClickEvent } from '@/scopes/prices/domain/analytics'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import { space, tok } from '@sunwebgroup/ui-components'
import { ToggleButton, ToggleButtonGroup, Typography } from '@sunwebgroup/ui-components/uds'
import { format } from 'date-fns'
import * as React from 'react'
import { styled } from 'styled-components'

const ToggleGroup = styled(ToggleButtonGroup)`
  display: flex;
  flex-direction: row;
  gap: ${space('primitives_space_24')};
`

const YearWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_6')};
`

const MonthsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${space('primitives_space_8')};
`

const MonthButton = styled(ToggleButton)`
  background: ${tok('semantics_views_background_color')};
  width: 76px; //Magic number from design
  flex: 1 0 0;
`

export function MonthFilterByWeeks() {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { selectFilterMonth } = React.useContext(PricesUseCasesContext)
  const { options } = usePricesSelector(getPriceRangeSelector)
  const { navigationDateTo, navigateToMonth } = React.useContext(NavigationByWeeksContext)
  const { selectedTab } = useGridTabsContext()

  const handleOnClick = (value: string) => {
    if (!value) {
      // when clicking on the active month, value is empty
      return
    }

    selectFilterMonth(value, navigateToMonth)

    sendEvent(
      createPricesFilterClickEvent({
        element: 'tab',
        type: 'month',
        result: value,
      }),
    )
  }

  const selectedMonth = React.useMemo(() => {
    if (!navigationDateTo || selectedTab === 'alertService')
      return

    const fromMonth = format(navigationDateTo, 'yyyy-MM')
    return Object.values(options)
      .flat()
      .find(({ value }) => value === fromMonth)
      ?.value
  }, [navigationDateTo, options, selectedTab])

  return (
    <>
      <ToggleGroup type="single" onValueChange={handleOnClick} value={selectedMonth}>
        {Object.keys(options).map((key: string) => (
          <YearWrapper key={key}>
            <Typography $variant="display-g-regular">{key}</Typography>
            <MonthsWrapper>
              {options[key].map(({ value }) => (
                <MonthButton key={value} value={value} title={capitalize((new Date(value)).toLocaleString(intl.locale, { month: 'short' }))} size="S" />
              ))}
            </MonthsWrapper>
          </YearWrapper>
        ))}
      </ToggleGroup>
    </>
  )
}
